import axios from 'axios';
import { bake } from './common';
import { toast } from 'react-toastify';
import { environmentLabels, environmentUrls } from './constants';
import { isPlaywrightTest } from './testConfig';

const environment = window.location.hostname.includes('elm.')
  ? 'production'
  : window.location.hostname.includes('elm-qa.')
    ? 'qa'
    : window.location.hostname.includes('elm-dev.')
      ? 'development'
      : 'local';
const host = environmentUrls[environment] ?? 'http://localhost:8080';
const ocpKey = '2a7bf31c098847e7943379cc001814d4';

// Function to append environment to email subject
const appendEnvironmentToSubject = (originalSubject) => {
  // Only append environment for non-production environments
  if (environment !== 'production' && originalSubject) {
    const envLabel = environmentLabels[environment] || 'UNKNOWN';
    return `[${envLabel}] ${originalSubject}`;
  }

  return originalSubject;
};

const fetchUserBusinessUnit = async (userPrincipalName) => {
  return axios
    .get(`${host}/workers/${userPrincipalName}/businessunit`, {
      headers: {
        'Ocp-Apim-Subscription-Key': ocpKey,
        // "Content-Type": "application/json",
        'Access-Control-Allow-Origin': '*',
      },
    })
    .then((response) => {
      if (response.data) {
        return response.data.BUSINESSUNIT;
      }
      return null;
    })
    .catch((err) => {
      console.error('Error fetching user business unit:', err);
    });
};

const fetchUserLastDay = async (userPrincipalName) => {
  return axios
    .get(`${host}/workers/${userPrincipalName}/lastDay`, {
      headers: {
        'Ocp-Apim-Subscription-Key': ocpKey,
        // "Content-Type": "application/json",
        'Access-Control-Allow-Origin': '*',
      },
    })
    .then((response) => {
      if (response.data) {
        return response.data.LAST_DAY;
      }
      return null;
    })
    .catch((err) => {
      console.error('Error fetching user last day:', err);
    });
};

const getUserContext = () => {
  const isKiosk = bake({ key: 'isKiosk' });
  const userOID = bake({ key: 'user' }) ? bake({ key: 'user' })['oid'] : '';

  if (isPlaywrightTest) {
    return { userMode: 'test', user: userOID };
  }

  if (isKiosk || userOID === 'kiosk') {
    return { userMode: 'kiosk', user: bake({ key: 'kioskUserOID' }) };
  }

  return { userMode: 'user', user: userOID };
};

const Api = async (body) => {
  const { userMode, user } = getUserContext();
  body.json.userMode = userMode;
  body.json.user = user;

  return axios
    .post(host + '/api', body, {
      headers: {
        'Ocp-Apim-Subscription-Key': ocpKey,
        // "Content-Type": "application/json",
        'Access-Control-Allow-Origin': '*',
      },
    })
    .then((response) => response.data)
    .catch((err) => console.error(err));
};

const Cosmos = async (body) => {
  body.user = bake({ key: 'user' })['oid'];

  return axios
    .post(host + '/cosmos', body, {
      headers: {
        'Ocp-Apim-Subscription-Key': ocpKey,
      },
    })
    .then((response) => response.data)
    .catch((err) => console.error(err));
};

const Query = async (body) => {
  body.user = bake({ key: 'user' })['oid'];
  return axios
    .post(host + '/query', body, {
      headers: {
        'Ocp-Apim-Subscription-Key': ocpKey,
      },
    })
    .then((response) => response.data)
    .catch((err) => console.error(err));
};

const QueryTable = async (body) => {
  body.user = bake({ key: 'user' })['oid'];
  return axios
    .post(host + '/querytable', body, {
      headers: {
        'Ocp-Apim-Subscription-Key': ocpKey,
      },
    })
    .then((response) => response.data)
    .catch((err) => console.error(err));
};

const ReportError = async (errorData) => {
  const { userMode, user } = getUserContext();

  return axios
    .post(
      host + '/error',
      { ...errorData, userMode, user },
      {
        headers: {
          'Ocp-Apim-Subscription-Key': ocpKey,
          'Access-Control-Allow-Origin': '*',
        },
      }
    )
    .then((response) => response.data)
    .catch((errErr) => console.error('Error reporting to swt server:', errErr));
};

const SendEmail = async (emailStoredProcedure, emailParams, interrupt) => {
  let emailObj = {};

  // Update emailSubject in emailParams before calling the stored procedure
  if (emailParams.emailSubject) {
    emailParams.emailSubject = appendEnvironmentToSubject(
      emailParams.emailSubject
    );
  }

  // Run the stored procedure associated with email using emailParams
  Api({
    sp: emailStoredProcedure,
    json: emailParams,
  }).then(async (response) => {
    // Save the database response to an email object
    // add email params sent at the initiation call, in case of attachment
    emailObj = {
      noBearerAuth: true,
      emailSubject: response[0].EmailSubject,
      emailContent: response[0].EmailContent,
      emailRecipients: response[0].EmailRecipients,
      emailAttachment: emailParams.emailAttachment ?? ' ',
      emailAttachmentName: emailParams.emailAttachmentName ?? ' ',
    };

    try {
      if (interrupt) {
        await interrupt.callBack();
      }
      // Call the Azure logic app with the emailObj to send the email
      return axios
        .post(bake({ key: 'instantEmailEndpoint' }), emailObj)
        .then((response) => {
          return response.data;
        })
        .catch((err) => {
          console.error(err);
          // Call the error endpoint on swt server
          return ReportError(err?.response?.data || err);
        });
    } catch (err) {
      toast.error(err);
    }
  });
};

const Upload = async (file) => {
  const fileType = file.type;

  const endpoint = fileType.startsWith('image/')
    ? '/upload/image'
    : '/upload/document';

  let formData = new FormData();
  formData.append(file.name, file);

  return await axios
    .post(host + endpoint, formData, {
      headers: {
        'Access-Control-Allow-Origin': '*',
        'Content-Type': 'multipart/form-data',
        'Access-Control-Allow-Credentials': true,
        'Ocp-Apim-Subscription-Key': ocpKey,
        supports_credentials: true,
      },
    })
    .then((response) => response)
    .catch((response) => response.statusText);
};

const fetchFile = async (fileName, type) => {
  const hostURL = `${host}/${type}/${fileName}`;
  if (!fileName || fileName === 'null') {
    console.error('No file name provided');
    return null;
  }
  const response = await axios.get(hostURL);

  const url = response.data.url;
  const fileBlobName = response.data.fileName;

  let finalUrl =
    url.URL + url.CONTAINER_NAME + '/' + fileBlobName + '?' + url.SAS_STRING;

  let originalName = url.ORIGINAL_NAME ?? fileBlobName;

  return { finalUrl, originalName };
};

const DownloadExcel = async (body) => {
  body.json.user = bake({ key: 'user' })['oid'];
  return axios
    .post(host + '/downloadExcel', body, {
      headers: {
        'Ocp-Apim-Subscription-Key': ocpKey,
      },
      responseType: 'arraybuffer',
    })
    .then((response) => response)
    .catch((err) => console.error(err));
};

const GeekApi = async (api, body) => {
  body.user = bake({ key: 'user' })['oid'];
  return axios
    .post(host + '/' + api, body, {
      headers: {
        'Ocp-Apim-Subscription-Key': ocpKey,
      },
    })
    .then((response) => response.data)
    .catch((err) => {
      let id = err?.response?.data?.error?.id;

      console.log('Error ' + JSON.stringify(err));
      throw new Error('Error processing Transaction ' + (id ? id : ''));
    });
};

export {
  Api,
  fetchUserBusinessUnit,
  Cosmos,
  Upload,
  fetchFile,
  Query,
  QueryTable,
  SendEmail,
  DownloadExcel,
  GeekApi,
  environment,
  appendEnvironmentToSubject,
  fetchUserLastDay, // Export the function for reuse
};
